import { calendarBars, clampDateTo, datePl, type MeDto, type MyCalendarDto, type OwnTaskDto, type SearchResultDto, shiftDate, todayInWarsaw } from '@bqcrm/shared';
import { ActionIcon, Box, Button, Checkbox, Group, Modal, Paper, Select, SimpleGrid, Stack, Text, Textarea, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { IconChevronLeft, IconChevronRight, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { ask } from '../confirm';
import { PageHeader } from '../components/PageHeader';
import { PartyPicker } from '../components/PartyPicker';
import { notifyError, notifyOk } from '../notify';
import { TaskPanel } from './planning/TaskPanel';

type Item = MyCalendarDto['items'][number];
const WEEKDAYS = ['pn', 'wt', 'śr', 'cz', 'pt', 'sb', 'nd'];
const MONTH = new Intl.DateTimeFormat('pl-PL', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const monthName = (d: string) => {
  const s = MONTH.format(new Date(`${d}T12:00:00Z`));
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const weekdayIdx = (d: string) => (new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7;

/** Kolory „light” Mantine — dopasowane do trybu jasnego i ciemnego. Kolor pozycji: moje wpisy — niebieski, zadania z planowania — zielony, nieobecność — pomarańczowy, wydarzenie — fiolet. */
function tone(i: Item): { bg: string; fg: string; bar: string } {
  if (i.own) return { bg: 'var(--mantine-color-blue-light)', fg: 'var(--mantine-color-blue-light-color)', bar: 'var(--mantine-color-blue-filled)' };
  if (i.kind === 'zadanie') return { bg: 'var(--mantine-color-green-light)', fg: 'var(--mantine-color-green-light-color)', bar: i.color ?? 'var(--mantine-color-green-filled)' };
  if (i.kind === 'nieobecnosc') return { bg: 'var(--mantine-color-orange-light)', fg: 'var(--mantine-color-orange-light-color)', bar: 'var(--mantine-color-orange-filled)' };
  if (i.kind === 'wydarzenie') return { bg: 'var(--mantine-color-violet-light)', fg: 'var(--mantine-color-violet-light-color)', bar: 'var(--mantine-color-violet-filled)' };
  return { bg: 'var(--mantine-color-gray-light)', fg: 'var(--mantine-color-gray-light-color)', bar: 'var(--mantine-color-gray-filled)' };
}

interface Form {
  title: string;
  dateFrom: string;
  dateTo: string;
  allDay: boolean;
  timeFrom: string;
  timeTo: string;
  partyId: string | null;
  partyName: string | null;
  caseId: string | null;
  caseLabel: string | null;
  place: string;
  note: string;
}
const blank = (day: string): Form => ({ title: '', dateFrom: day, dateTo: day, allDay: false, timeFrom: '10:00', timeTo: '11:00', partyId: null, partyName: null, caseId: null, caseLabel: null, place: '', note: '' });
const fromDto = (o: OwnTaskDto): Form => ({
  title: o.title,
  dateFrom: o.dateFrom,
  dateTo: o.dateTo,
  allDay: !o.timeFrom,
  timeFrom: o.timeFrom ?? '10:00',
  timeTo: o.timeTo ?? '11:00',
  partyId: o.caseId ? null : o.partyId,
  partyName: o.partyName,
  caseId: o.caseId,
  caseLabel: o.caseNumber ? `${o.caseNumber}${o.partyName ? ` · ${o.partyName}` : ''}` : null,
  place: o.place,
  note: o.note,
});

/** Wybór sprawy z wyszukiwaniem (numer, klient, tytuł). */
function CasePicker({ value, initialLabel, onChange }: { value: string | null; initialLabel: string | null; onChange: (id: string | null, label: string | null) => void }) {
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 300);
  const q = useQuery({ queryKey: ['case-picker', debounced], queryFn: () => api<SearchResultDto>(`/search?q=${encodeURIComponent(debounced)}`), enabled: debounced.trim().length >= 2 });
  const options = (q.data?.cases ?? []).map((c) => ({ value: c.id, label: `${c.number} · ${c.clientName}` }));
  if (value && initialLabel && !options.some((o) => o.value === value)) options.unshift({ value, label: initialLabel });
  return (
    <Select
      label="Sprawa (opcjonalnie)"
      placeholder="Numer sprawy albo klient…"
      searchable
      clearable
      data={options}
      value={value}
      onChange={(v) => onChange(v, options.find((o) => o.value === v)?.label ?? null)}
      onSearchChange={setSearch}
      searchValue={search}
      nothingFoundMessage={debounced.length >= 2 ? 'Nie znaleziono' : 'Wpisz co najmniej 2 znaki'}
      filter={({ options: o }) => o}
    />
  );
}

function OwnDialog({ edit, day, onClose }: { edit: OwnTaskDto | null; day: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<Form>(edit ? fromDto(edit) : blank(day));
  const set = (p: Partial<Form>) => setF((x) => ({ ...x, ...p }));
  const body = () => ({
    title: f.title,
    dateFrom: f.dateFrom,
    dateTo: f.dateTo,
    timeFrom: f.allDay ? null : f.timeFrom,
    timeTo: f.allDay ? null : f.timeTo,
    partyId: f.caseId ? null : f.partyId,
    caseId: f.caseId,
    place: f.place,
    note: f.note,
  });
  const done = (msg: string) => {
    notifyOk(msg);
    void qc.invalidateQueries({ queryKey: ['my-calendar'] });
    onClose();
  };
  const save = useMutation({
    mutationFn: () => (edit ? api<OwnTaskDto>(`/me/calendar/own/${edit.id}`, { method: 'PUT', body: body() }) : api<OwnTaskDto>('/me/calendar/own', { method: 'POST', body: body() })),
    onSuccess: () => done(edit ? 'Zapisano zmiany' : 'Dodano wpis — trafi też na telefon do „Moich zadań”'),
    onError: notifyError,
  });
  const remove = useMutation({ mutationFn: () => api<void>(`/me/calendar/own/${edit!.id}`, { method: 'DELETE' }), onSuccess: () => done('Usunięto wpis'), onError: notifyError });
  return (
    <Modal opened onClose={onClose} title={edit ? 'Mój wpis' : 'Nowy wpis'} size="lg">
      <Stack>
        <TextInput label="Tytuł" placeholder="np. Wizyta u klienta — oferta klimatyzacji" value={f.title} onChange={(e) => set({ title: e.currentTarget.value })} data-autofocus required />
        <Group grow align="flex-end">
          <TextInput type="date" label="Od" value={f.dateFrom} onChange={(e) => set({ dateFrom: e.currentTarget.value, dateTo: clampDateTo(e.currentTarget.value, f.dateTo) })} />
          <TextInput type="date" label="Do" value={f.dateTo} min={f.dateFrom || undefined} onChange={(e) => set({ dateTo: e.currentTarget.value })} />
        </Group>
        <Group align="flex-end">
          <Checkbox label="Cały dzień" checked={f.allDay} onChange={(e) => set({ allDay: e.currentTarget.checked })} mb={8} />
          {!f.allDay && (
            <>
              <TextInput type="time" label="Od godz." value={f.timeFrom} onChange={(e) => set({ timeFrom: e.currentTarget.value })} w={120} />
              <TextInput type="time" label="Do godz." value={f.timeTo} onChange={(e) => set({ timeTo: e.currentTarget.value })} w={120} />
            </>
          )}
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <CasePicker value={f.caseId} initialLabel={f.caseLabel} onChange={(caseId, caseLabel) => set({ caseId, caseLabel })} />
          {!f.caseId && <PartyPicker label="Klient (opcjonalnie)" value={f.partyId} initialLabel={f.partyName} onChange={(partyId) => set({ partyId })} />}
        </SimpleGrid>
        <TextInput label="Miejsce" placeholder="puste — adres z klienta lub sprawy" value={f.place} onChange={(e) => set({ place: e.currentTarget.value })} />
        <Textarea label="Notatka" autosize minRows={2} value={f.note} onChange={(e) => set({ note: e.currentTarget.value })} />
        <Text size="xs" c="dimmed">
          Wpis trafi na Twój telefon do „Moich zadań” (bez czynności i protokołu) i zamknie się sam następnego dnia rano. Inni widzą w kalendarzu tylko, że jesteś zajęty{f.caseId ? '; w historii sprawy — „Zaplanowano: …”' : ''}.
        </Text>
        <Group justify="space-between">
          {edit ? (
            <Button variant="subtle" color="red" leftSection={<IconTrash size={16} />} loading={remove.isPending} onClick={() => void ask('Wpis zniknie z kalendarza i z telefonu.', { title: 'Usunąć wpis?', confirm: 'Usuń' }).then((ok) => ok && remove.mutate())}>
              Usuń
            </Button>
          ) : (
            <span />
          )}
          <Group>
            <Button variant="default" onClick={onClose}>
              Anuluj
            </Button>
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              Zapisz
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

/** „Mój kalendarz” (docs/22): miesiąc jednej osoby — zadania, nieobecności, wydarzenia i własne wpisy; dodawanie wpisów. */
export function MyCalendarPage({ me }: { me: MeDto }) {
  const today = todayInWarsaw();
  const narrow = useMediaQuery('(max-width: 48em)');
  const [month, setMonth] = useState(today.slice(0, 7) + '-01');
  const first = month;
  const start = shiftDate(first, -weekdayIdx(first));
  const end = shiftDate(start, 41);
  const days = Array.from({ length: 42 }, (_, n) => shiftDate(start, n));
  const q = useQuery({ queryKey: ['my-calendar', start, end], queryFn: () => api<MyCalendarDto>(`/me/calendar?from=${start}&to=${end}`) });
  const [dialog, setDialog] = useState<{ edit: OwnTaskDto | null; day: string } | null>(null);
  const [task, setTask] = useState<string | null>(null);
  const shift = (n: number) => {
    const d = new Date(`${month}T12:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() + n);
    setMonth(d.toISOString().slice(0, 7) + '-01');
  };
  const open = (i: Item) => {
    if (i.own) {
      const o = q.data?.own.find((x) => x.id === i.refId);
      if (o) setDialog({ edit: o, day: o.dateFrom });
    } else if (i.kind === 'zadanie' && i.refId) setTask(i.refId);
  };
  const all = q.data?.items ?? [];
  /** drugi rząd wpisu: godziny (albo „cały dzień”) i szczegół — klient / miejsce, stan zadania, rodzaj wpisu */
  const subLine = (i: Item) => {
    const hours = i.timeFrom ? `${i.timeFrom.slice(0, 5)}–${(i.timeTo ?? '').slice(0, 5)}` : 'cały dzień';
    let detail = '';
    if (i.own) {
      const o = q.data?.own.find((x) => x.id === i.refId);
      detail = [o?.partyName, o?.place || o?.caseNumber].filter(Boolean).join(' · ');
    } else if (i.kind === 'zadanie') detail = i.status === 'zakonczone' ? 'zakończone' : i.status === 'w_toku' ? 'w toku' : '';
    else if (i.kind === 'nieobecnosc') detail = i.deputyName ? `zastępuje: ${i.deputyName}` : 'nieobecność';
    else if (i.kind === 'wydarzenie') detail = 'wydarzenie firmowe';
    return [hours, detail].filter(Boolean).join(' · ');
  };

  return (
    <>
      <PageHeader
        title="Mój kalendarz"
        description="Twoje zadania, nieobecności, wydarzenia firmowe i własne wpisy"
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => setDialog({ edit: null, day: today })}>
            Nowy wpis
          </Button>
        }
      />
      <Group mb="sm" gap="xs">
        <ActionIcon variant="default" onClick={() => shift(-1)} aria-label="Poprzedni miesiąc">
          <IconChevronLeft size={18} />
        </ActionIcon>
        <Text fw={700} w={170} ta="center">
          {monthName(month)}
        </Text>
        <ActionIcon variant="default" onClick={() => shift(1)} aria-label="Następny miesiąc">
          <IconChevronRight size={18} />
        </ActionIcon>
        <Button variant="default" size="xs" onClick={() => setMonth(today.slice(0, 7) + '-01')}>
          Dziś
        </Button>
      </Group>
      <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
        <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
          {WEEKDAYS.map((w) => (
            <Text key={w} size="xs" c="dimmed" ta="center" py={4} bg="var(--app-subtle)" style={{ borderBottom: '1px solid var(--app-line-strong)' }}>
              {w}
            </Text>
          ))}
        </Box>
        {Array.from({ length: 6 }, (_, w) => days.slice(w * 7, w * 7 + 7)).map((week) => {
          // jak w kalendarzu firmy: kolejne dni tego samego wpisu całodniowego (urlop, wydarzenie, wpis kilkudniowy) — jeden słupek
          const bars = calendarBars(week, all);
          const lanes = Math.max(1, ...bars.map((x) => x.lane + 1));
          return (
            <Box key={week[0]} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gridTemplateRows: `${narrow ? 24 : 30}px repeat(${lanes}, auto) minmax(8px, 1fr)`, rowGap: 3, minHeight: narrow ? 70 : 130 }}>
              {week.map((d, n) => {
                const inMonth = d.slice(0, 7) === month.slice(0, 7);
                return (
                  <Box
                    key={d}
                    onClick={() => setDialog({ edit: null, day: d })}
                    style={{
                      gridColumn: n + 1,
                      gridRow: '1 / -1',
                      padding: 4,
                      borderRight: '1px solid var(--app-line)',
                      borderBottom: '1px solid var(--app-line)',
                      // poza miesiącem — przygaszone (jak w kalendarzu firmy), dziś — wyróżnione; w obu trybach kolorów
                      background: d === today ? 'var(--app-today)' : inMonth ? undefined : 'var(--app-subtle)',
                      opacity: inMonth ? 1 : 0.55,
                      cursor: 'pointer',
                      minWidth: 0,
                    }}
                  >
                    <Text size="xs" fw={d === today ? 700 : 400} c={d === today ? 'white' : inMonth ? undefined : 'dimmed'} bg={d === today ? 'blue' : undefined} w={22} ta="center" style={{ borderRadius: 999 }}>
                      {Number(d.slice(8))}
                    </Text>
                  </Box>
                );
              })}
              {bars.map((x) => {
                const i = x.item as Item;
                const t = tone(i);
                const done = i.status === 'zakonczone';
                const r = `${x.startsHere ? 4 : 0}px ${x.endsHere ? 4 : 0}px ${x.endsHere ? 4 : 0}px ${x.startsHere ? 4 : 0}px`;
                return (
                  <Tooltip key={`${i.refId}:${i.day}:${x.col}`} label={`${i.timeFrom ? `${i.timeFrom.slice(0, 5)}–${(i.timeTo ?? '').slice(0, 5)} ` : ''}${i.title}`} openDelay={400}>
                    <UnstyledButton
                      onClick={(e) => {
                        e.stopPropagation();
                        open(i);
                      }}
                      style={{
                        gridColumn: `${x.col + 1} / span ${x.span}`,
                        gridRow: x.lane + 2,
                        zIndex: 1,
                        minWidth: 0,
                        marginLeft: x.startsHere ? 3 : 0,
                        marginRight: x.endsHere ? 3 : 0,
                        background: t.bg,
                        color: t.fg,
                        borderLeft: x.startsHere ? `3px solid ${t.bar}` : undefined,
                        borderRadius: r,
                        padding: narrow ? '1px 4px' : '4px 8px',
                        overflow: 'hidden',
                        opacity: done ? 0.55 : 1,
                      }}
                    >
                      {/* jak w kalendarzu firmy: tytuł, pod nim godziny i szczegół (na wąskim ekranie — jeden rząd) */}
                      <Text size={narrow ? 'xs' : 'sm'} fw={600} lh={narrow ? '16px' : '20px'} truncate td={done ? 'line-through' : undefined} c="inherit">
                        {narrow && i.timeFrom ? `${i.timeFrom.slice(0, 5)} ` : ''}
                        {i.title}
                      </Text>
                      {!narrow && (
                        <Text size="xs" lh="16px" truncate c="inherit" style={{ opacity: 0.85 }}>
                          {subLine(i)}
                        </Text>
                      )}
                    </UnstyledButton>
                  </Tooltip>
                );
              })}
            </Box>
          );
        })}
      </Paper>
      <Group gap="md" mt="xs">
        {[
          ['Moje wpisy', 'blue'],
          ['Zadania z planowania', 'green'],
          ['Nieobecność', 'orange'],
          ['Wydarzenie firmowe', 'violet'],
        ].map(([l, c]) => (
          <Group key={l} gap={4}>
            <Box w={10} h={10} bg={`var(--mantine-color-${c}-6)`} style={{ borderRadius: 2 }} />
            <Text size="xs" c="dimmed">
              {l}
            </Text>
          </Group>
        ))}
        <Text size="xs" c="dimmed">
          Kliknij dzień, aby dodać wpis · zakończone — przekreślone · {datePl(today)}
        </Text>
      </Group>
      {dialog && <OwnDialog key={dialog.edit?.id ?? dialog.day} edit={dialog.edit} day={dialog.day} onClose={() => setDialog(null)} />}
      {task && <TaskPanel me={me} taskId={task} onClose={() => setTask(null)} />}
    </>
  );
}
